import type HotelPackage from '../hotel/HotelPackage';
import type RoomsPackage from '../hotel/RoomsPackage';
import type ItineraryOrder from '../ItineraryOrder';
import ItineraryPackage from '../ItineraryPackage';
import type PriceClass from '../PriceClass';
import type PriceType from '../PriceType';
import type TripPackage from '../trip/TripPackage';
import { type PassengerCount } from '../types';
import { type BundleRoomsPaxCount } from './types';
import type VerifyResult from '@itinerary/VerifyResult';
import { type InvoiceEntry } from '@store/invoice';
import { ReserveType } from '@utils/itinerary';
import type FlightPackageSet from '@itinerary/trip/flight/FlightPackageSet';

export default class BundlePackage extends ItineraryPackage<PriceClass> {
    private readonly _hotelPackage: HotelPackage;
    private readonly _departurePackageSet: FlightPackageSet;
    private readonly _returnPackageSet: FlightPackageSet;
    private readonly _hotelRoomsPackages: RoomsPackage[];
    private readonly _rooms: BundleRoomsPaxCount[];
    private readonly _defaultDeparturePackage: TripPackage;
    private readonly _defaultReturnPackage: TripPackage;
    private readonly _defaultRoomsPackage: RoomsPackage;
    override singlePrice: number;
    override singlePayable: number;
    sumTotalPrice: number;

    constructor({
        hotelPackage,
        departurePackageSet,
        returnPackageSet,
        rooms,
        singlePayable,
        singlePrice,
        roomsPackages,
        sumTotalPrice,
    }: {
        hotelPackage: HotelPackage;
        departurePackageSet: FlightPackageSet;
        returnPackageSet: FlightPackageSet;
        rooms: BundleRoomsPaxCount[];
        singlePrice: number;
        singlePayable: number;
        roomsPackages: RoomsPackage[];
        sumTotalPrice: number;
    }) {
        super();
        this._hotelPackage = hotelPackage;
        this._departurePackageSet = departurePackageSet;
        this._returnPackageSet = returnPackageSet;
        this._defaultDeparturePackage = departurePackageSet.list[0]!;
        this._defaultReturnPackage = returnPackageSet.list[0]!;
        this._rooms = rooms;
        this.singlePrice = singlePrice;
        this.singlePayable = singlePayable;
        this._hotelRoomsPackages = roomsPackages;
        [this._defaultRoomsPackage] = roomsPackages;
        this.sumTotalPrice = sumTotalPrice;
    }

    get defaultDeparturePackage(): TripPackage {
        return this._defaultDeparturePackage;
    }

    get defaultReturnPackage(): TripPackage {
        return this._defaultReturnPackage;
    }

    get defaultRoomsPackage(): RoomsPackage {
        return this._defaultRoomsPackage;
    }

    get departurePackageSet(): FlightPackageSet {
        return this._departurePackageSet;
    }

    get returnPackageSet(): FlightPackageSet {
        return this._returnPackageSet;
    }

    get hotelRoomsPackages(): RoomsPackage[] {
        return this._hotelRoomsPackages;
    }

    get roomsCount(): number {
        return this._rooms.length;
    }

    get totalAdultCount(): number {
        return this._rooms.reduce((all, curr) => all + curr.adultCount, 0);
    }

    get totalChildCount(): number {
        return this._rooms.reduce((all, curr) => all + curr.childCount, 0);
    }

    get totalInfantCount(): number {
        return this._rooms.reduce((all, curr) => all + curr.infantCount, 0);
    }

    get totalPaxCounts(): number {
        return (
            this.totalAdultCount + this.totalChildCount + this.totalInfantCount
        );
    }

    get hotelPackage(): HotelPackage {
        return this._hotelPackage;
    }

    override get invoiceTitle(): string {
        throw new Error('The method is not implemented.');
    }

    override get selectedPricesWithPriceClass():
        | [PriceClass, PriceType<PriceClass>][]
        | null {
        throw new Error('The method is not implemented.');
    }

    override get raiseTitle(): string {
        throw new Error('The method is not implemented.');
    }

    override get termsAndConditions(): string {
        throw new Error('The method is not implemented.');
    }

    override get totalCount(): number {
        throw new Error('The method is not implemented.');
    }

    override get points(): number | null {
        throw new Error('The method is not implemented.');
    }

    override get prices(): PriceType<PriceClass>[] {
        throw new Error('The method is not implemented.');
    }

    override get order(): ItineraryOrder {
        throw new Error('The method is not implemented.');
    }

    get needsLoginForDiscount(): boolean {
        throw new Error('The method is not implemented.');
    }

    override get buyTooltip(): string {
        throw new Error('The method is not implemented.');
    }

    override get totalPayableForCheck(): number | null {
        throw new Error('The method is not implemented.');
    }

    override get totalPriceForCheck(): number | null {
        throw new Error('The method is not implemented.');
    }

    override get reservable(): boolean {
        return this.hotelPackage.reservable;
    }

    override get key(): string {
        return this.hotelPackage.key;
    }

    override get reserveType(): ReserveType {
        return ReserveType.Bundle;
    }

    override get callToReserve(): boolean {
        throw new Error('The method is not implemented.');
    }

    override createBill(
        _count: PassengerCount,
        _billId: number | null
    ): Promise<VerifyResult> {
        throw new Error('The method is not implemented.');
    }

    override loadPreInvoice(_count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
        loggedIn: boolean;
    }): Promise<InvoiceEntry[]> {
        throw new Error('The method is not implemented.');
    }
}
